// Ubicación en el repo: src/components/CatalogFormModal.jsx
// Modal Crear/Editar producto del catálogo. Reescrito en la Fase A del
// módulo de Inventario/Contabilidad (migración 0046, 24-ago-2026, ver
// plan-inventario-y-contabilidad.md): tipo (enum fijo) y marca (texto
// libre) pasan a ser selects sobre categorías/marcas administrables desde
// las pestañas de al lado (Catalog.jsx) — se agregan proveedor, precio de
// costo, SKU y el margen calculado.
//
// IMPORTANTE: al guardar, además de category_id/brand_id, este formulario
// SIGUE escribiendo catalog.type/catalog.brand (texto) con el nombre de la
// categoría/marca elegida — HearingAidFormModal.jsx (venta de audífonos,
// flujo en uso real) todavía depende de esas dos columnas de texto
// (`c.type === 'Audífono'`, agrupación por `c.brand`) y no se tocó en esta
// fase por ser un flujo de venta activo. Ver nota igual en lib/catalog.js.
//
// Fase B (migración 0049, 25-ago-2026): el stock deja de vivir en
// `catalog.stock`/`catalog.stock_min` (ahora congeladas) — pasa a
// `catalog_branch_stock`, una fila por sucursal. Por eso acá "Cantidad
// inicial"/"Stock mínimo" solo aparecen al CREAR un producto nuevo (se
// cargan a la sucursal activa, `branchId`, justo después de crear la fila
// en `catalog`); al editar, el stock ya no se toca desde este formulario —
// se ajusta con "📦 Ajustar stock" o se mueve con "🔁 Traspasar" en la
// tabla de Catalog.jsx.
import { useEffect, useMemo, useState } from 'react';
import { createCatalogItem, updateCatalogItem, setBranchStock } from '../lib/catalog';
import { listActiveCategories } from '../lib/categories';
import { listActiveBrands } from '../lib/brands';
import { listActiveSuppliers } from '../lib/suppliers';
import { formatCurrency } from '../lib/format';
import { getSignedCatalogImageUrl, removeCatalogImage, uploadCatalogImage } from '../lib/catalogImages';

// Borrador de condiciones de garantía (migración 0052, 16-sep-2026) — pedido
// de Nicolás: quería que se redactara un texto razonable en vez de partir de
// un campo en blanco. Se precarga solo en un producto NUEVO al marcarlo como
// Audífono (ver el efecto más abajo que lo hace) — es un punto de partida,
// no un texto fijo: cada producto lo puede editar libremente, y un producto
// ya existente nunca se pisa solo. Mismo criterio de "borrador técnico a
// revisar" que consentDocument.js: cubre lo esperable de una garantía de
// audífonos, pero no reemplaza una revisión propia de las condiciones reales
// que ofrece cada marca/proveedor.
const DEFAULT_WARRANTY_TERMS = `Esta garantía cubre defectos de fabricación y fallas de funcionamiento del audífono no atribuibles al uso, durante el período indicado, contado desde la fecha de venta. Incluye reparación o reemplazo del equipo, sin costo, cuando la falla corresponda a un defecto cubierto.

No cubre: daño por exposición a agua o humedad (salvo que el modelo cuente con certificación de resistencia al agua vigente para ese caso); golpes, caídas o daño físico; pérdida o robo; desgaste normal de piezas de uso (domos, filtros de cerumen, pilas); reparaciones, aperturas o modificaciones realizadas por personal no autorizado; ni fallas producidas por un uso distinto al indicado por el fabricante.

Para hacer efectiva la garantía, presente este documento junto con el audífono en la sucursal donde realizó la compra.`;

function emptyForm() {
  return {
    categoryId: '',
    brandId: '',
    supplierId: '',
    model: '',
    sku: '',
    price: '',
    costPrice: '',
    description: '',
    warrantyTerms: '',
    stock: '0',
    stockMin: '2',
    powerType: 'Pila',
  };
}

function itemToForm(item) {
  return {
    categoryId: item.category_id || '',
    brandId: item.brand_id || '',
    supplierId: item.supplier_id || '',
    model: item.model ?? '',
    sku: item.sku ?? '',
    price: item.price ?? '',
    costPrice: item.cost_price ?? '',
    description: item.description ?? '',
    warrantyTerms: item.warranty_terms ?? '',
    powerType: item.power_type || 'Pila',
  };
}

export default function CatalogFormModal({ item, branchId, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState(() => (isEdit ? itemToForm(item) : emptyForm()));
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Foto de producto (migración 0051, 26-ago-2026 — usada en Catálogo y en
  // la cotización imprimible). Mismo patrón que el logo de sucursal en
  // BranchFormModal.jsx: `imageFile` es lo elegido en este paso (todavía
  // no subido), se sube recién al guardar porque la ruta de Storage
  // necesita el id del producto, que en un producto nuevo recién existe
  // después de crear la fila en `catalog`.
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    if (isEdit && item.image_path) {
      getSignedCatalogImageUrl(item.image_path).then(setImagePreviewUrl);
    }
  }, [isEdit, item]);

  useEffect(() => {
    Promise.all([listActiveCategories(), listActiveBrands(), listActiveSuppliers()])
      .then(([cats, brs, sups]) => {
        setCategories(cats);
        setBrands(brs);
        setSuppliers(sups);
      })
      .catch((err) => setError(err.message || 'No se pudieron cargar categorías/marcas/proveedores.'))
      .finally(() => setLoadingLists(false));
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError('');
    setRemoveImage(false);
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  }

  function handleRemoveImage() {
    setImageFile(null);
    setImagePreviewUrl(null);
    setRemoveImage(true);
  }

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === form.categoryId) || null,
    [categories, form.categoryId]
  );
  // Rol especial (migración 0050, 25-ago-2026) — ya no compara el nombre
  // exacto "Audífono", así la categoría se puede renombrar sin romper el
  // toggle Pila/Recargable. Ver CategoryFormModal.jsx.
  const isHearingAid = selectedCategory?.special_role === 'audifono';

  // Precarga el borrador de condiciones de garantía (ver constante arriba) —
  // solo en un producto NUEVO, solo la primera vez que se marca como
  // Audífono, y solo si el campo sigue vacío. Nunca pisa lo que ya haya
  // escrito la persona ni lo que ya traiga un producto existente.
  useEffect(() => {
    if (!isEdit && isHearingAid) {
      setForm((f) => (f.warrantyTerms ? f : { ...f, warrantyTerms: DEFAULT_WARRANTY_TERMS }));
    }
  }, [isEdit, isHearingAid]);

  const margin = useMemo(() => {
    const price = Number(form.price) || 0;
    const cost = Number(form.costPrice) || 0;
    if (!price || !cost) return null;
    const diff = price - cost;
    const pct = (diff / price) * 100;
    return { diff, pct };
  }, [form.price, form.costPrice]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.categoryId) {
      setError('Elige una categoría.');
      return;
    }
    if (!form.brandId) {
      setError('Elige una marca.');
      return;
    }
    if (!form.model.trim()) {
      setError('Indica el modelo.');
      return;
    }
    setSaving(true);
    try {
      const selectedBrand = brands.find((b) => b.id === form.brandId);
      const values = {
        category_id: form.categoryId,
        brand_id: form.brandId,
        supplier_id: form.supplierId || null,
        // Se mantienen sincronizados por compatibilidad — ver comentario al
        // inicio del archivo.
        type: selectedCategory?.name ?? null,
        brand: selectedBrand?.name ?? null,
        model: form.model.trim().toUpperCase(),
        sku: form.sku.trim() || null,
        price: form.price !== '' ? Number(form.price) : 0,
        cost_price: form.costPrice !== '' ? Number(form.costPrice) : null,
        description: form.description.trim() || null,
        // Solo se guarda para Audífonos — un producto de otra categoría no
        // tiene "venta y garantía" asociada en el sistema hoy, así que no
        // tiene sentido arrastrar el texto si se cambia la categoría después.
        warranty_terms: isHearingAid ? form.warrantyTerms.trim() || null : null,
        power_type: isHearingAid ? form.powerType : 'Pila',
      };
      let catalogId = item?.id;
      if (isEdit) {
        await updateCatalogItem(item.id, values);
      } else {
        catalogId = await createCatalogItem(values);
        // Stock inicial: se carga a la sucursal activa (Fase B) — si el
        // producto también se vende en otras sucursales, se traspasa desde
        // ahí, o se ajusta entrando primero a esa sucursal.
        if (branchId) {
          await setBranchStock(catalogId, branchId, form.stock, form.stockMin);
        }
      }

      // La foto se maneja aparte — mismo motivo que el logo de sucursal
      // (ver comentario donde se declara imageFile más arriba).
      if (imageFile) {
        try {
          await uploadCatalogImage(catalogId, imageFile, isEdit ? item.image_path : null);
        } catch (imgErr) {
          setImageError(imgErr.message || 'No se pudo subir la foto, pero el producto se guardó.');
        }
      } else if (removeImage && isEdit && item.image_path) {
        try {
          await removeCatalogImage(catalogId, item.image_path);
        } catch (imgErr) {
          setImageError(imgErr.message || 'No se pudo quitar la foto.');
        }
      }

      onSaved();
    } catch (err) {
      setError(err.message || 'No se pudo guardar el producto.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h2>{isEdit ? 'Editar producto' : 'Nuevo producto de catálogo'}</h2>

        <form onSubmit={handleSubmit} className="patient-form">
          <div className="form-row">
            <label className="field">
              <span>Categoría *</span>
              <select value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)} disabled={loadingLists}>
                <option value="">—</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className="field-hint">Se administran en la pestaña "Categorías", al lado.</span>
            </label>
            <label className="field">
              <span>Marca *</span>
              <select value={form.brandId} onChange={(e) => set('brandId', e.target.value)} disabled={loadingLists}>
                <option value="">—</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <span className="field-hint">Se administran en la pestaña "Marcas", al lado.</span>
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Modelo *</span>
              <input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="Modelo" />
            </label>
            <label className="field">
              <span>SKU / código interno</span>
              <input value={form.sku} onChange={(e) => set('sku', e.target.value)} placeholder="Opcional" />
            </label>
          </div>

          <label className="field">
            <span>Proveedor habitual</span>
            <select value={form.supplierId} onChange={(e) => set('supplierId', e.target.value)} disabled={loadingLists}>
              <option value="">Sin especificar</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <span className="field-hint">Se administran en la pestaña "Proveedores", al lado. Opcional.</span>
          </label>

          {isHearingAid && (
            <div className="field">
              <span>Alimentación</span>
              <div className="checkbox-group">
                {['Pila', 'Recargable'].map((pt) => (
                  <label key={pt} className="field--checkbox">
                    <input
                      type="radio"
                      name="powerType"
                      checked={form.powerType === pt}
                      onChange={() => set('powerType', pt)}
                    />
                    {pt}
                  </label>
                ))}
              </div>
              <span className="field-hint">
                Recargable = este modelo viene de fábrica en pack de 2 y se factura como un pack. Una venta
                "Bilateral" va a descontar 1 unidad de stock (el pack completo) y va a usar el valor de abajo
                como precio total, en vez de tratarlo como 2 audífonos con precio individual.
              </span>
            </div>
          )}

          {isHearingAid && (
            <label className="field">
              <span>Condiciones de garantía</span>
              <textarea
                value={form.warrantyTerms}
                onChange={(e) => set('warrantyTerms', e.target.value)}
                rows={6}
                placeholder="Qué cubre la garantía, qué no cubre, y cómo se reclama…"
              />
              <span className="field-hint">
                Se precargó un borrador general — revísalo y ajústalo a las condiciones reales de este modelo
                antes de guardar. Aparece impreso en el documento de garantía de cada venta de este producto
                (botón "Imprimir garantía" en Audífonos → Ventas), junto a la foto de arriba.
              </span>
            </label>
          )}

          <div className="form-row">
            <label className="field">
              <span>Precio de venta (CLP)</span>
              <input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="0" />
            </label>
            <label className="field">
              <span>Precio de costo (CLP)</span>
              <input type="number" value={form.costPrice} onChange={(e) => set('costPrice', e.target.value)} placeholder="Opcional" />
            </label>
          </div>
          {margin && (
            <p className="field-hint" style={{ marginTop: -8 }}>
              Margen: {formatCurrency(margin.diff)} ({margin.pct.toFixed(1)}%)
            </p>
          )}

          <label className="field">
            <span>Descripción / características</span>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              placeholder="Ej: Cancelación de ruido, conectividad Bluetooth, recargable 24h de autonomía…"
            />
            <span className="field-hint">
              Opcional — aparece en la cotización imprimible de Audífonos, junto a la foto.
            </span>
          </label>

          {/* Ver mismo comentario en BranchFormModal.jsx sobre por qué el
              botón "Quitar foto" va aparte del input, no envuelto en un
              <label> junto con él. */}
          <div className="field">
            <span>Foto del producto</span>
            {imagePreviewUrl && (
              <img
                src={imagePreviewUrl}
                alt="Foto del producto"
                style={{ maxHeight: 100, maxWidth: 160, objectFit: 'contain', marginBottom: 6, display: 'block' }}
              />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageChange} />
            <span className="field-hint">Aparece en la cotización imprimible de Audífonos. Máximo 2 MB.</span>
            {imagePreviewUrl && (
              <button type="button" className="link-button link-button--danger" onClick={handleRemoveImage} style={{ marginTop: 4 }}>
                Quitar foto
              </button>
            )}
            {imageError && <p className="auth-error">{imageError}</p>}
          </div>

          {isEdit ? (
            <p className="field-hint">
              El stock se administra por sucursal desde la tabla de Catálogo ("📦 Ajustar stock" / "🔁
              Traspasar"), no desde este formulario.
            </p>
          ) : (
            <div className="form-row">
              <label className="field">
                <span>Cantidad inicial</span>
                <input type="number" value={form.stock} onChange={(e) => set('stock', e.target.value)} />
                <span className="field-hint">
                  {isHearingAid && form.powerType === 'Recargable'
                    ? 'Cada unidad de stock es 1 pack (2 audífonos). Se carga a la sucursal activa.'
                    : 'Cada unidad de stock es 1 unidad individual. Se carga a la sucursal activa.'}
                </span>
              </label>
              <label className="field">
                <span>Stock mínimo (alerta)</span>
                <input type="number" value={form.stockMin} onChange={(e) => set('stockMin', e.target.value)} />
              </label>
            </div>
          )}

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving || loadingLists}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
